// Day view — one focused day: all-day section, timed section, big empty state.
// A «+» button and the empty state itself offer quick-create for this day.

import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from '@govard/i18n-core/react';
import { Popover } from 'antd';
import { ClipboardList, Users } from 'lucide-react';
import { useMemo, useState, type ReactElement } from 'react';

import {
  formatDayTitle,
  groupEventsByDay,
  type PlannerEvent,
  type PlannerViewHandlers,
} from '../lib/plannerEvents.js';
import { PlannerEventCard } from './PlannerEventCard.js';
import type { QuickCreateKind, QuickCreateRequest } from './PlannerWeekView.js';

interface DayViewProps extends PlannerViewHandlers {
  events: PlannerEvent[];
  date: Dayjs;
  onQuickCreate?: ((request: QuickCreateRequest) => void) | undefined;
}

function Section({
  title,
  events,
  onOpen,
  onToggleTask,
}: PlannerViewHandlers & { title: string; events: PlannerEvent[] }): ReactElement {
  return (
    <div>
      <div
        className="mb-2 text-[11px] font-semibold uppercase tracking-[1px]"
        style={{ color: 'var(--ant-color-text-tertiary)' }}
      >
        {title}
      </div>
      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 2xl:grid-cols-3">
        {events.map((event) => (
          <PlannerEventCard
            key={event.id}
            event={event}
            onOpen={onOpen}
            onToggleTask={onToggleTask}
          />
        ))}
      </div>
    </div>
  );
}

export function PlannerDayView({
  events,
  date,
  onOpen,
  onToggleTask,
  onQuickCreate,
}: DayViewProps): ReactElement {
  const { t, locale } = useTranslation();
  const [quickOpen, setQuickOpen] = useState(false);
  const dateKey = date.format('YYYY-MM-DD');
  const group = useMemo(
    () => groupEventsByDay(events).get(dateKey) ?? { allDay: [], timed: [] },
    [events, dateKey],
  );
  const isToday = dateKey === dayjs().format('YYYY-MM-DD');
  const total = group.allDay.length + group.timed.length;

  const requestQuickCreate = (kind: QuickCreateKind): void => {
    setQuickOpen(false);
    onQuickCreate?.({ kind, dateKey });
  };

  const quickCreateContent: ReactElement = (
    <div className="flex flex-col" style={{ minWidth: 132 }}>
      <button
        type="button"
        onClick={() => requestQuickCreate('task')}
        className="crm-hover-fill flex items-center gap-2 border-0 bg-transparent px-2.5 py-1.5 text-left text-[12px] font-medium transition-colors duration-[110ms]"
        style={{ color: 'var(--ant-color-text)', borderRadius: 'var(--ant-border-radius-sm, 6px)' }}
      >
        <ClipboardList
          size={13}
          style={{ color: 'var(--ant-color-primary-border-hover, #d97706)' }}
        />
        {t('crm.calendar.quick_create.task')}
      </button>
      <button
        type="button"
        onClick={() => requestQuickCreate('meeting')}
        className="crm-hover-fill flex items-center gap-2 border-0 bg-transparent px-2.5 py-1.5 text-left text-[12px] font-medium transition-colors duration-[110ms]"
        style={{ color: 'var(--ant-color-text)', borderRadius: 'var(--ant-border-radius-sm, 6px)' }}
      >
        <Users size={13} style={{ color: 'var(--ant-color-primary-border-hover, #d97706)' }} />
        {t('crm.calendar.quick_create.meeting')}
      </button>
    </div>
  );

  return (
    <div className="max-w-[1400px]">
      <div className="mb-3.5 mt-0.5 flex flex-wrap items-center gap-2.5">
        <span className="text-[15px] font-semibold" style={{ color: 'var(--ant-color-text)' }}>
          {formatDayTitle(date, locale, dayjs())}
        </span>
        {isToday ? (
          <span
            className="rounded-full px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide"
            style={{
              backgroundColor: 'var(--ant-color-primary)',
              color: 'var(--crm-on-accent, #ffffff)',
            }}
          >
            {t('crm.calendar.nav.today')}
          </span>
        ) : null}
        {onQuickCreate ? (
          <Popover
            open={quickOpen}
            onOpenChange={setQuickOpen}
            content={quickCreateContent}
            trigger="click"
            placement="bottomLeft"
            styles={{ content: { padding: 4 } }}
          >
            <button
              type="button"
              title={t('crm.calendar.quick_create.add', {
                date: date.locale(locale).format('D MMMM'),
              })}
              onClick={() => setQuickOpen(true)}
              className="flex h-[22px] w-[22px] items-center justify-center border-0 bg-transparent text-[15px] font-semibold leading-none transition-colors duration-[110ms] hover:bg-[var(--color-surface-hover,var(--g-fill))]"
              style={{
                color: 'var(--ant-color-text-tertiary)',
                borderRadius: 'var(--ant-border-radius-sm, 6px)',
              }}
              aria-label={t('crm.calendar.quick_create.add', {
                date: date.locale(locale).format('D MMMM'),
              })}
            >
              +
            </button>
          </Popover>
        ) : null}
      </div>

      {total === 0 ? (
        <div
          className="py-16 text-center text-[13px]"
          style={{ color: 'var(--ant-color-text-tertiary)' }}
          onClick={
            onQuickCreate
              ? (e) => {
                  if (e.target === e.currentTarget) setQuickOpen(true);
                }
              : undefined
          }
        >
          {t('crm.calendar.empty_day_view')}
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {group.allDay.length > 0 ? (
            <Section
              title={t('crm.calendar.zone.all_day')}
              events={group.allDay}
              onOpen={onOpen}
              onToggleTask={onToggleTask}
            />
          ) : null}
          {group.timed.length > 0 ? (
            <Section
              title={t('crm.calendar.day_sections.timed')}
              events={group.timed}
              onOpen={onOpen}
              onToggleTask={onToggleTask}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
